import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { SuggestResult } from '../../shared/asset-constants';

// Search-Autocomplete Popover (Sprint Quick-Wins Item 1).
//
// ARIA 1.2 Combobox-Pattern (Listbox-Popup):
// - Parent (LibraryPage search-input) trägt role=combobox, aria-expanded,
//   aria-controls, aria-activedescendant.
// - Diese Component rendert <ul role=listbox> mit role=option Items.
// - Keyboard: Arrow-Up/Down navigation (managed im LibraryPage via callback),
//   Enter committed, Escape schließt.
//
// Debounce 200ms (kürzer als die 300ms der Library-fetch — Suggest soll
// schneller reagieren). AbortController canceled stale requests.
//
// Status-Filter: Suggest-Endpoint ist IMMER approved-only (Codex A7) —
// kein status-Param hier nötig.
//
// activeIndex-Reset bei jedem neuen Response (Sonnet R1 M1) — verhindert
// stale-aria-activedescendant.

const DEBOUNCE_MS = 200;
const MIN_Q_LENGTH = 2;

type Props = {
  query: string;
  /** ID des popovers (für aria-controls am parent input). */
  listboxId: string;
  /** Click oder Enter auf einer Suggestion → commit-callback. */
  onCommit: (label: string) => void;
  /** Esc oder Click-outside → close. */
  onClose: () => void;
  /**
   * Externer Keyboard-State: parent gibt activeIndex + setActiveIndex,
   * weil parent (input) auch arrow-keys handelt + an aria-activedescendant
   * binden muss.
   */
  activeIndex: number;
  setActiveIndex: (idx: number) => void;
  /**
   * Setter callback für Total-Count → parent kann z.B. aria-expanded
   * managen oder den max-index für Keyboard-nav kennen.
   */
  onTotalChange: (total: number) => void;
};

type Item = {
  id: string;
  type: 'filename' | 'tag' | 'uploader';
  label: string;
  hint?: string;
};

function flatten(result: SuggestResult): Item[] {
  return [
    ...result.filenames.map((f) => ({
      id: `f:${f.id}`,
      type: 'filename' as const,
      label: f.displayFilename,
    })),
    ...result.tags.map((t) => ({
      id: `t:${t.name}`,
      type: 'tag' as const,
      label: t.name,
      hint: `${t.count} Asset${t.count === 1 ? '' : 's'}`,
    })),
    ...result.uploaders.map((u) => ({
      id: `u:${u.id}`,
      type: 'uploader' as const,
      label: u.displayName,
    })),
  ];
}

export function SearchSuggestPopover({
  query,
  listboxId,
  onCommit,
  onClose,
  activeIndex,
  setActiveIndex,
  onTotalChange,
}: Props): React.JSX.Element | null {
  const [items, setItems] = useState<Item[]>([]);
  const [hasResponded, setHasResponded] = useState(false);

  // Debounced fetch + AbortController. Symmetric pattern zu existing
  // Library-fetch in LibraryPage.
  useEffect(() => {
    if (query.trim().length < MIN_Q_LENGTH) {
      setItems([]);
      setHasResponded(false);
      onTotalChange(0);
      return;
    }
    const ctrl = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const result = await api<SuggestResult>(
          `/api/assets/search/suggest?q=${encodeURIComponent(query.trim())}`,
          { signal: ctrl.signal },
        );
        const flat = flatten(result);
        setItems(flat);
        setHasResponded(true);
        onTotalChange(flat.length);
        // activeIndex reset (Sonnet R1 M1) — neuer Response invalidiert
        // alte option-IDs.
        setActiveIndex(-1);
      } catch (err) {
        // AbortError = normaler Cancel, silent (cleanup läuft, parent
        // unmount oder neuer query started — alte items behalten OK).
        if (err instanceof Error && err.name === 'AbortError') return;
        // Network/Server-Error: stale items wegräumen damit User keine
        // alten Treffer für neue query sieht (Codex R2 [P2]). Empty-State
        // rendert "Keine Treffer für '...'" — semantisch unscharf bei
        // Network-Error, aber korrekter als stale-data.
        setItems([]);
        setHasResponded(true);
        onTotalChange(0);
        setActiveIndex(-1);
      }
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // Outside-Click Handler — close-callback.
  useEffect(() => {
    function onClick(e: MouseEvent) {
      const target = e.target as Node;
      const popover = document.getElementById(listboxId);
      // Auch input selbst NICHT als outside zählen (input ist parent — focus
      // bleibt drin).
      const input = document.querySelector('[role="combobox"]') as HTMLElement | null;
      if (popover && !popover.contains(target) && !input?.contains(target)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [listboxId, onClose]);

  // Render-Bedingungen:
  // - q < 2 chars → null (kein Popover)
  // - hasResponded && empty → "Keine Treffer für '...'"
  // - hasResponded && items > 0 → Listbox mit Items
  if (query.trim().length < MIN_Q_LENGTH) return null;
  if (!hasResponded) return null; // initial fetch pending → nichts zeigen

  return (
    <div
      id={listboxId}
      role="listbox"
      aria-label="Suchvorschläge"
      className="absolute top-full left-0 right-0 mt-1 z-30 bg-white border border-ink-200 rounded-lg shadow-lg max-h-[400px] overflow-y-auto"
    >
      {items.length === 0 ? (
        <div role="status" className="px-4 py-3 text-sm text-brand-green-600">
          Keine Treffer für „{query.trim()}"
        </div>
      ) : (
        items.map((item, idx) => {
          const isActive = idx === activeIndex;
          // Index-based ID (NICHT entity-prefixed) damit aria-activedescendant
          // im LibraryPage-Input matched (`suggest-option-${suggestActiveIdx}`).
          // Sonnet PR-Review Important — ARIA-1.2 requires id-match für SR-announce.
          const optionId = `suggest-option-${idx}`;
          return (
            <button
              key={item.id}
              id={optionId}
              role="option"
              aria-selected={isActive}
              type="button"
              onMouseEnter={() => setActiveIndex(idx)}
              onClick={(e) => {
                e.preventDefault();
                onCommit(item.label);
              }}
              className={`w-full text-left px-4 py-2 text-sm flex items-center justify-between gap-2 ${
                isActive ? 'bg-brand-cream' : 'hover:bg-brand-cream'
              }`}
            >
              <span className="flex items-center gap-2 min-w-0">
                <TypeBadge item={item} />
                <span className="text-brand-green-700 truncate">{item.label}</span>
              </span>
              {item.hint && (
                <span className="text-2xs text-brand-green-600 font-mono shrink-0">
                  {item.hint}
                </span>
              )}
            </button>
          );
        })
      )}
    </div>
  );
}

const TYPE_BADGE_CONFIG: Record<Item['type'], { label: string; color: string }> = {
  filename: { label: 'DATEI', color: 'bg-brand-green-700 text-brand-cream' },
  tag: { label: 'TAG', color: 'bg-brand-yellow text-brand-green-700' },
  uploader: { label: 'USER', color: 'bg-brand-cream text-brand-green-700' },
};

// Endung aus dem Dateinamen ableiten (z.B. „…-4.jpg" → „JPG"). Suggest-API
// liefert nur displayFilename (kein mimeType), daher rein client-seitig.
// Kein Punkt / Endung > 4 Zeichen → generisches „DATEI" als Fallback.
function fileExtBadge(filename: string): string {
  const dot = filename.lastIndexOf('.');
  if (dot >= 0) {
    const ext = filename.slice(dot + 1).toUpperCase();
    if (ext.length >= 1 && ext.length <= 4) return ext;
  }
  return TYPE_BADGE_CONFIG.filename.label;
}

function TypeBadge({ item }: { item: Item }) {
  const cfg = TYPE_BADGE_CONFIG[item.type];
  // Bei Datei-Treffern den konkreten Typ (JPG/PNG/MP4/PDF …) statt „DATEI".
  const label = item.type === 'filename' ? fileExtBadge(item.label) : cfg.label;
  return (
    <span
      className={`px-1.5 py-0.5 rounded font-mono text-2xs tracking-wide shrink-0 ${cfg.color}`}
      aria-hidden="true"
    >
      {label}
    </span>
  );
}
